import React, { forwardRef } from 'react'

import { Xmark } from '@gravity-ui/icons'
import { cn,DateField, DateRangePicker as HeroDateRangePicker, I18nProvider, Label, RangeCalendar } from '@heroui/react'
import { parseDate } from '@internationalized/date'

type DateRangeValue = {
  start: string;
  end: string;
} | null;

type DateRangePickerProps = {
  label?: string;
  value?: DateRangeValue;
  onChange?: (val: DateRangeValue) => void;
  className?: string;
  variant?: 'primary' | 'secondary' | 'ghost';
  name?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  onBlur?: React.FocusEventHandler;
  minValue?: string;
  maxValue?: string;
  placeholderValue?: string;
  disableNavigation?: boolean;
};

export const DateRangePicker = forwardRef<HTMLDivElement, DateRangePickerProps>(({
  label,
  value,
  onChange,
  className,
  variant = 'secondary',
  name,
  isRequired,
  isDisabled,
  isInvalid,
  onBlur,
  minValue,
  maxValue,
  placeholderValue,
  disableNavigation = false,
}, ref) => {

  const minDate = React.useMemo(() => {
    if (!minValue) return undefined
    try {
      return parseDate(minValue)
    } catch {
      return undefined
    }
  }, [minValue])

  const maxDate = React.useMemo(() => {
    if (!maxValue) return undefined
    try {
      return parseDate(maxValue)
    } catch {
      return undefined
    }
  }, [maxValue])

  const placeholderDate = React.useMemo(() => {
    if (!placeholderValue) return undefined
    try {
      return parseDate(placeholderValue)
    } catch {
      return undefined
    }
  }, [placeholderValue])

  // Convert string (YYYY-MM-DD) range to CalendarDate range
  const rangeValue = React.useMemo(() => {
    if (!value?.start || !value.end) return null
    try {
      return {
        start: parseDate(value.start),
        end: parseDate(value.end),
      }
    } catch {
      return null
    }
  }, [value])

  const handleChange = (range: any) => {
    if (typeof onChange === 'function') {
      if (range?.start && range.end) {
        onChange({
          start: range.start.toString(),
          end: range.end.toString(),
        })
      } else {
        onChange(null)
      }
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (typeof onChange === 'function') {
      onChange(null)
    }
  }

  return (
    <I18nProvider locale={'ru-RU'}>
      <HeroDateRangePicker
        ref={ref}
        className={cn('w-full', className as any)}
        value={rangeValue}
        onChange={handleChange}
        isRequired={isRequired}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        onBlur={onBlur as any}
        minValue={minDate}
        maxValue={maxDate}
        placeholderValue={placeholderDate}
        {...({ name } as any)}
      >
        {label && <Label>{label}</Label>}

        <DateField.Group fullWidth variant={variant as any}>
          <DateField.Input slot="start">
            {segment => <DateField.Segment segment={segment} />}
          </DateField.Input>
          <HeroDateRangePicker.RangeSeparator />
          <DateField.Input slot="end">
            {segment => <DateField.Segment segment={segment} />}
          </DateField.Input>
          <DateField.Suffix>
            {value?.start && value.end && !isDisabled && (
              <button
                type="button"
                onClick={handleClear}
                className="cursor-pointer text-zinc-400 hover:text-zinc-600 transition-colors p-0.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 mr-1 flex items-center justify-center"
                aria-label="Clear date range"
              >
                <Xmark className="size-3.5" />
              </button>
            )}
            <HeroDateRangePicker.Trigger>
              <HeroDateRangePicker.TriggerIndicator />
            </HeroDateRangePicker.Trigger>
          </DateField.Suffix>
        </DateField.Group>

        <HeroDateRangePicker.Popover>
          <RangeCalendar aria-label={label || 'Date range'}>
            <RangeCalendar.Header>
              <RangeCalendar.YearPickerTrigger
                className={disableNavigation ? "pointer-events-none hover:bg-transparent cursor-default bg-transparent shadow-none" : undefined}
              >
                <RangeCalendar.YearPickerTriggerHeading />
                {!disableNavigation && <RangeCalendar.YearPickerTriggerIndicator />}
              </RangeCalendar.YearPickerTrigger>
              {!disableNavigation && <RangeCalendar.NavButton slot="previous" />}
              {!disableNavigation && <RangeCalendar.NavButton slot="next" />}
            </RangeCalendar.Header>
            <RangeCalendar.Grid>
              <RangeCalendar.GridHeader>
                {day => <RangeCalendar.HeaderCell>{day}</RangeCalendar.HeaderCell>}
              </RangeCalendar.GridHeader>
              <RangeCalendar.GridBody>
                {date => <RangeCalendar.Cell date={date} />}
              </RangeCalendar.GridBody>
            </RangeCalendar.Grid>
            <RangeCalendar.YearPickerGrid>
              <RangeCalendar.YearPickerGridBody>
                {({ year }) => <RangeCalendar.YearPickerCell year={year} />}
              </RangeCalendar.YearPickerGridBody>
            </RangeCalendar.YearPickerGrid>
          </RangeCalendar>
        </HeroDateRangePicker.Popover>
      </HeroDateRangePicker>
    </I18nProvider>
  )
})

DateRangePicker.displayName = 'DateRangePicker'
